/**
 * Job-index regions.
 *
 * The job index is deployed per region: one query serves exactly one country,
 * and a country belongs to exactly one region (east = current India cluster,
 * west = Americas). Multi-country requests are never sent to the API; callers
 * that need data for several countries issue one request per country and
 * combine the results client-side.
 *
 * The west list mirrors artha-ai's routing policy (packages/artha_eal/config).
 */

export type JobIndexRegion = "east" | "west";

export const WEST_COUNTRIES: ReadonlySet<string> = new Set([
  "AI", "AG", "AR", "AW", "BS", "BB", "BZ", "BM", "BO", "BQ", "BR", "CA", "KY", "CL",
  "CO", "CR", "CU", "CW", "DM", "DO", "EC", "SV", "FK", "GF", "GL", "GD", "GP", "GT",
  "GY", "HT", "HN", "JM", "MQ", "MX", "MS", "NI", "PA", "PY", "PE", "PR", "BL", "KN",
  "LC", "MF", "PM", "VC", "SX", "GS", "SR", "TT", "TC", "US", "UY", "VE", "VG", "VI",
]);

const LEGACY_ALIASES: Record<string, string> = { UK: "GB", USA: "US" };

export const toRegionCountryCode = (value: unknown): string | undefined => {
  const code = String(value ?? "").trim().toUpperCase();
  if (!code) return undefined;
  return LEGACY_ALIASES[code] ?? code;
};

/** Region that serves a country. Anything not in the Americas is east. */
export const regionForCountry = (country: unknown): JobIndexRegion => {
  const code = toRegionCountryCode(country);
  return code && WEST_COUNTRIES.has(code) ? "west" : "east";
};

/**
 * Splits a `location` value (string "IN,US", array, or single code) into
 * distinct ISO2 codes, preserving order.
 */
export const splitLocation = (value: unknown): string[] => {
  const raw = Array.isArray(value) ? value : String(value ?? "").split(",");
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of raw) {
    const code = toRegionCountryCode(entry);
    if (code && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
};

/**
 * Picks the ONE country a search may be sent for. Prefers `preferred` (e.g. the
 * viewer's country) when it is in the selection, otherwise the first entry.
 * Returns undefined when there is no country at all.
 */
export const pickSingleCountry = (value: unknown, preferred?: unknown): string | undefined => {
  const codes = splitLocation(value);
  if (!codes.length) return undefined;
  const wanted = toRegionCountryCode(preferred);
  return wanted && codes.includes(wanted) ? wanted : codes[0];
};
